import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePersonas, useCreatePersona } from "../../hooks/usePersona";
import { StateBadge } from "../../components/StateBadge";
import { PageHeader } from "../../components/ui/PageHeader";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { deletePersona, type PersonaState } from "../../api/personas";
import { ApiError } from "../../lib/api";
import type { StateBadgeKind } from "../../components/StateBadge";
import { useIsMobile } from "../../lib/useMediaQuery";

// ─── Helpers ─────────────────────────────────────────────────────────────────────

function stateToBadgeKind(state: PersonaState): StateBadgeKind {
  switch (state) {
    case "active":  return "active";
    case "draft":   return "draft";
    case "retired": return "retired";
  }
}

function stateLabel(state: PersonaState): string {
  switch (state) {
    case "active":  return "ACTIVE";
    case "draft":   return "DRAFT";
    case "retired": return "RETIRED";
  }
}

// ─── Component ───────────────────────────────────────────────────────────────────

export function PersonasList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError } = usePersonas();
  const createMutation = useCreatePersona();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newSynthetic, setNewSynthetic] = useState(true);
  const isMobile = useIsMobile();

  const [confirmPersona, setConfirmPersona] = useState<{ id: string; name: string } | null>(null);
  const deleteMut = useMutation({
    mutationFn: (id: string) => deletePersona(id),
    onSuccess: () => {
      setConfirmPersona(null);
      void qc.invalidateQueries({ queryKey: ["personas"] });
    },
    onError: (err) =>
      alert(
        err instanceof ApiError && err.status === 409
          ? "This persona is in use by an active engagement and can't be deleted."
          : err instanceof ApiError
            ? err.message
            : "Failed to delete persona.",
      ),
  });

  if (isLoading) {
    return (
      <div style={{ padding: 24, color: "var(--text-muted)", fontFamily: "var(--font-sans)" }}>
        Loading personas…
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: 24, color: "var(--error)", fontFamily: "var(--font-sans)" }}>
        Failed to load personas.
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div style={{ fontFamily: "var(--font-sans)", maxWidth: 960 }}>
      <PageHeader
        eyebrow="Operator console"
        title="Persona registry"
        subtitle="Authored agent personas — voice clones, system prompts, and RoE-bound instructions used during engagements."
        meta={
          <span>
            <span style={{ color: "var(--text-secondary)" }}>{data?.total ?? 0}</span> total
          </span>
        }
      />

      {/* Create button / inline form */}
      {!showCreate ? (
        <button
          onClick={() => setShowCreate(true)}
          style={{
            marginBottom: 12,
            padding: "7px 16px",
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            border: "1px solid var(--accent)",
            background: "var(--accent)",
            color: "white",
            cursor: "pointer",
          }}
        >
          + New persona
        </button>
      ) : (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
            display: "flex",
            gap: 10,
            alignItems: "flex-end",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
            <span style={{ fontSize: 9.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-muted)" }}>Display name</span>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Sarah Thompson"
              autoFocus
              style={{
                background: "var(--bg-input)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "7px 10px",
                color: "var(--text-primary)",
                fontSize: 12,
                fontFamily: "var(--font-sans)",
                outline: "none",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
            <span style={{ fontSize: 9.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-muted)" }}>Role</span>
            <input
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              placeholder="e.g. IT Help Desk Analyst"
              style={{
                background: "var(--bg-input)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "7px 10px",
                color: "var(--text-primary)",
                fontSize: 12,
                fontFamily: "var(--font-sans)",
                outline: "none",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, alignSelf: "center" }}>
            <input
              type="checkbox"
              checked={newSynthetic}
              onChange={(e) => setNewSynthetic(e.target.checked)}
              style={{ accentColor: "var(--accent)", width: 14, height: 14, cursor: "pointer" }}
            />
            <span style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap" }}>Synthetic</span>
          </div>
          <button
            disabled={!newName.trim() || !newRole.trim() || createMutation.isPending}
            onClick={() => {
              createMutation.mutate(
                { display_name: newName.trim(), role: newRole.trim(), synthetic: newSynthetic },
                {
                  onSuccess: (data) => {
                    setShowCreate(false);
                    setNewName("");
                    setNewRole("");
                    navigate(`/personas/${data.record.persona_id}`);
                  },
                },
              );
            }}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--accent)",
              background: !newName.trim() || !newRole.trim() ? "var(--bg-tertiary)" : "var(--accent)",
              color: !newName.trim() || !newRole.trim() ? "var(--text-muted)" : "white",
              cursor: !newName.trim() || !newRole.trim() ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {createMutation.isPending ? "Creating..." : "Create"}
          </button>
          <button
            onClick={() => { setShowCreate(false); setNewName(""); setNewRole(""); setNewSynthetic(true); }}
            style={{
              padding: "7px 12px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--glass-border)",
            borderRadius: 12,
            padding: "48px 24px",
            textAlign: "center",
            color: "var(--text-muted)",
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          <div style={{ marginBottom: 8 }}>No personas registered.</div>
          <code
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--text-secondary)",
              background: "var(--bg-tertiary)",
              padding: "3px 8px",
              borderRadius: 4,
            }}
          >
            python -m tools.register_persona
          </code>
        </div>
      ) : isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((item) => (
            <div
              key={item.persona_id}
              onClick={() => navigate(`/personas/${item.persona_id}`)}
              style={{
                background: "var(--bg-card)",
                border: "1px solid var(--glass-border)",
                borderRadius: 12,
                padding: 14,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                    {item.display_name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                    {item.role}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StateBadge kind={stateToBadgeKind(item.state)}>
                    {stateLabel(item.state)}
                  </StateBadge>
                  <button
                    type="button"
                    aria-label={`Delete ${item.display_name}`}
                    title="Delete persona"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmPersona({ id: item.persona_id, name: item.display_name });
                    }}
                    style={{
                      display: "grid",
                      placeItems: "center",
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      border: "1px solid var(--border-subtle)",
                      background: "transparent",
                      color: "var(--error)",
                      cursor: "pointer",
                      fontSize: 13,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                Voice:{" "}
                <span style={{ color: item.has_voice_ref ? "var(--success)" : "var(--text-muted)", fontWeight: 600 }}>
                  {item.has_voice_ref ? "cloned ✓" : "none —"}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            background: "var(--bg-card)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid var(--glass-border)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          {/* Table header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 140px 80px 36px",
              gap: 12,
              padding: "10px 16px",
              borderBottom: "1px solid var(--border-subtle)",
              background: "var(--bg-secondary)",
            }}
          >
            {["Name · Role", "State", "Voice"].map((h) => (
              <span
                key={h}
                style={{
                  fontSize: 9.5,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "var(--text-muted)",
                }}
              >
                {h}
              </span>
            ))}
            <span />
          </div>

          {/* Table rows */}
          {items.map((item) => (
            <div
              key={item.persona_id}
              onClick={() => navigate(`/personas/${item.persona_id}`)}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 140px 80px 36px",
                gap: 12,
                padding: "12px 16px",
                borderBottom: "1px solid var(--border-subtle)",
                cursor: "pointer",
                alignItems: "center",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background =
                  "var(--bg-hover)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "transparent";
              }}
            >
              {/* Name + role */}
              <div>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "var(--text-primary)",
                  }}
                >
                  {item.display_name}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: "var(--text-muted)",
                    marginTop: 2,
                  }}
                >
                  {item.role}
                </div>
              </div>

              {/* State pill */}
              <div>
                <StateBadge kind={stateToBadgeKind(item.state)}>
                  {stateLabel(item.state)}
                </StateBadge>
              </div>

              {/* Voice ref indicator */}
              <div
                style={{
                  fontSize: 13,
                  color: item.has_voice_ref ? "var(--success)" : "var(--text-muted)",
                  fontWeight: 600,
                }}
              >
                {item.has_voice_ref ? "✓" : "—"}
              </div>

              {/* Delete button */}
              <button
                type="button"
                aria-label={`Delete ${item.display_name}`}
                title="Delete persona"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmPersona({ id: item.persona_id, name: item.display_name });
                }}
                style={{
                  display: "grid",
                  placeItems: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: "1px solid var(--border-subtle)",
                  background: "transparent",
                  color: "var(--error)",
                  cursor: "pointer",
                  fontSize: 13,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmPersona !== null}
        title="Delete persona?"
        body={
          <>
            Delete <strong>{confirmPersona?.name}</strong>? Past calls keep their
            snapshotted copy of this persona. This cannot be undone.
          </>
        }
        busy={deleteMut.isPending}
        onConfirm={() => confirmPersona && deleteMut.mutate(confirmPersona.id)}
        onCancel={() => setConfirmPersona(null)}
      />
    </div>
  );
}
